/**
 * ชนิดข้อมูลของ Customer Master — เขียนตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Customer Master — Payload Contract (D-089)")
 *
 * **ไฟล์นี้เป็นฝาแฝดของ `lib/suppliers/types.ts` โดยตั้งใจ** ทั้งสองคือทะเบียนคู่ค้า
 * ระดับองค์กรที่เอกสารธุรกรรมอ้างด้วย business code — contract เองก็เขียนไว้ว่า
 * "มิเรอร์ Supplier Master (D-083) แบบตั้งใจ" สิ่งที่ต่างกันจึงควรมีเหตุผล ไม่ใช่
 * ต่างเพราะเขียนคนละวัน (และไม่มีการ import ข้ามกัน: ทะเบียนหนึ่งเปลี่ยนตาม contract
 * ของตัวเองได้โดยไม่ลากอีกทะเบียนไปด้วย)
 *
 * Customer เป็น master **ระดับองค์กร** ไม่มี `plant_id` และไม่มีมุมมองรายโรงงาน
 * — ต่างจาก Sales Order (P2-4b) ที่ผูกกับโรงงานเดียวทุกใบ นั่นเป็นเหตุผลที่หน้าจอ
 * ของมันไม่มีตัวเลือกโรงงานเลย
 *
 * `customer_code` เป็น **business key ที่แก้ไม่ได้** (D-089): Sales Order, lot และ
 * การส่งของที่อ้างถึงมันต้อง trace ได้ต่อไปตลอด และการกันซ้ำ Customer PO ด้วย
 * `UNIQUE(customer_id, customer_po_number)` จะไม่มีความหมายเลยถ้า key ขยับได้
 * รหัสจึงถูกเขียนครั้งเดียวตอนสร้างและไม่เคยเดินทางไปกับ `PATCH` อีก — ดู
 * `UpdateCustomerBody` ด้านล่างซึ่ง **ไม่มีคีย์นั้นอยู่ในชนิดข้อมูล** ไม่ใช่แค่ไม่ส่ง
 *
 * **ไม่มี DELETE โดยตั้งใจ** เลิกค้าขายคือ `PATCH status = INACTIVE` ไม่ใช่การลบแถว
 * ออกจากใต้เอกสารที่อ้างมันอยู่
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — โมดูล **ธุรกิจ** ปกติ ADMIN ถูก deny (D-060) */
export const CUSTOMER_MASTER_MODULE = 'CUSTOMER_MASTER';

/* ------------------------------------------------------------- สถานะ ----- */

export const ACTIVE_CUSTOMER_STATUS = 'ACTIVE';
export const INACTIVE_CUSTOMER_STATUS = 'INACTIVE';
export const BLOCKED_CUSTOMER_STATUS = 'BLOCKED';

/**
 * สามค่าที่ CHECK constraint ฝั่งเซิร์ฟเวอร์รับ ตามลำดับที่ contract เขียนไว้
 *
 * `BLOCKED` **ไม่ใช่คำพ้องของ `INACTIVE`**: ลูกค้าที่ inactive คือความสัมพันธ์ที่จบ
 * ไปแล้ว ส่วนลูกค้าที่ blocked คือความสัมพันธ์ที่ยังอยู่แต่ถูกระงับไว้ การห้ามเปิด SO
 * ใหม่ให้ลูกค้า `BLOCKED` (`422 CUSTOMER_IS_BLOCKED`) ถูกบังคับที่ P2-4b (Sales Order)
 * ไม่ใช่ที่นี่ — กฎนั้นเป็นของเอกสารธุรกรรม ไม่ใช่ของทะเบียน
 */
export const CUSTOMER_STATUSES = [
  ACTIVE_CUSTOMER_STATUS,
  INACTIVE_CUSTOMER_STATUS,
  BLOCKED_CUSTOMER_STATUS,
] as const;

export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

/** ค่าที่เซิร์ฟเวอร์เติมให้เมื่อ `POST` ไม่ได้ส่ง `status` มา */
export const DEFAULT_CUSTOMER_STATUS = ACTIVE_CUSTOMER_STATUS;

export function isCustomerStatus(value: string): value is CustomerStatus {
  return (CUSTOMER_STATUSES as readonly string[]).includes(value);
}

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/** `customer_code` ≤ 20 และ `customer_name` ≤ 200 — ตรงกับคอลัมน์จริงฝั่งเซิร์ฟเวอร์ */
export const CUSTOMER_CODE_MAX_LENGTH = 20;
export const CUSTOMER_NAME_MAX_LENGTH = 200;

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `409` เมื่อรหัสลูกค้านั้นมีเจ้าของอยู่แล้ว — **ไม่มีอะไรถูกเขียนเลย**
 *
 * เป็นคนละเรื่องกับความล้มเหลวของระบบ: ทางแก้อยู่ในมือผู้ใช้ทันที (ใช้รหัสอื่น
 * หรือไปแก้ลูกค้ารายที่ถือรหัสนี้อยู่แล้ว) การยุบมันเข้ากับ `server-error` คือการ
 * ส่งเขาไปโทรหาฝ่ายไอที
 */
export const CUSTOMER_CODE_TAKEN = 'CUSTOMER_CODE_TAKEN';

/**
 * `400` เมื่อ body ของ `PATCH` มี `customer_code` ติดไปด้วย
 *
 * หน้าจอนี้ไม่มีเส้นทางที่ประกอบคีย์นั้นเข้า body ได้เลย (ดู `UpdateCustomerBody`)
 * การที่มันเกิดขึ้นจริงจึงแปลว่าหน้าจอกับ API ไม่ตรงกันแล้ว — เป็นบั๊กที่ต้องเห็น
 * ไม่ใช่ความล้มเหลวที่กลืนเงียบ ๆ
 */
export const CUSTOMER_CODE_IS_IMMUTABLE = 'CUSTOMER_CODE_IS_IMMUTABLE';

/**
 * `400` เมื่อ body มี `customer_id`/`created_at`/`updated_at` ซึ่งเซิร์ฟเวอร์เป็น
 * เจ้าของ — ด้วยเหตุผลเดียวกับข้อข้างบน ที่นี่ไม่มีทางประกอบมันขึ้นมาได้
 */
export const CUSTOMER_FIELD_IS_SERVER_OWNED = 'CUSTOMER_FIELD_IS_SERVER_OWNED';

/* ------------------------------------------------------------- resource --- */

/** จำนวนช่องผู้ติดต่อที่ `contact_no` รับได้ (D-121) — 1 ถึง 3 ไม่ใช่รายการไม่จำกัด */
export const CUSTOMER_CONTACT_NO_MINIMUM = 1;
export const CUSTOMER_CONTACT_NO_MAXIMUM = 3;

/** ความยาวสูงสุดของแต่ละช่องผู้ติดต่อ — ตรงกับคอลัมน์จริงฝั่งเซิร์ฟเวอร์ (D-125) */
export const CUSTOMER_CONTACT_NAME_MAX_LENGTH = 200;
export const CUSTOMER_CONTACT_EMAIL_MAX_LENGTH = 255;
export const CUSTOMER_CONTACT_PHONE_MAX_LENGTH = 30;

/**
 * หนึ่งในผู้ติดต่อไม่เกินสามรายของลูกค้า (D-121)
 *
 * `contact_no` เดินทางมากับทุกแถว **ไม่ใช่ตำแหน่งในรายการ**: มันเป็นครึ่งหนึ่งของ
 * primary key ฝั่งเซิร์ฟเวอร์ (`UNIQUE(customer_id, contact_no)`) และหน้าจอที่
 * เขียนว่า "ผู้ติดต่อ 2" ต้องรู้ว่ากำลังพูดถึงช่องไหนจริง ๆ ไม่ใช่ช่องที่สองที่
 * เผอิญถูกส่งมา
 *
 * `email`/`phone` เป็น `null` ได้ทั้งคู่ — ผู้ติดต่อที่มีแต่ชื่อยังเป็นข้อมูลที่ใช้ได้
 */
export interface CustomerContact {
  readonly contact_no: number;
  readonly contact_name: string;
  readonly email: string | null;
  readonly phone: string | null;
}

/**
 * หนึ่งลูกค้าตามที่ contract ระบุ — ไม่มี `plant_code`/`plant_id`
 *
 * `address` และ `contacts` ถูกเพิ่มโดย D-121 และประกาศเป็น **optional** ที่นี่
 * โดยตั้งใจ: คำตอบจาก API รุ่นก่อน D-121 ไม่มีสองคีย์นี้เลย และ `parseCustomer`
 * ต้องอ่านมันได้ต่อไปแทนที่จะทิ้งทั้งรายการ (รายการที่ขาดไปหนึ่งรายอ่านเหมือน
 * "ยังไม่มีลูกค้ารายนี้" แล้วผู้ใช้จะสร้างรหัสที่มีเจ้าของอยู่แล้วซ้ำเข้าไป)
 * — ตัว parser normalize ให้เป็น `null`/`[]` เสมอ ผู้อ่านจึงไม่ต้องแยกสามกรณี
 *
 * `address` เป็น **ข้อความก้อนเดียวที่มี line break ได้** ไม่ใช่ที่อยู่แบบแยกฟิลด์
 * (D-121) — หน้าจอที่แสดงมันต้องคง line break ไว้ ไม่ใช่ยุบเป็นบรรทัดเดียว
 */
export interface Customer {
  /** UUID surrogate ที่เซิร์ฟเวอร์สร้าง (D-039) — ใช้เป็น path ของ `GET`/`PATCH` */
  readonly customer_id: string;
  readonly customer_code: string;
  readonly customer_name: string;
  readonly status: string;
  readonly address?: string | null;
  readonly contacts?: readonly CustomerContact[];
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/customers` — envelope มาตรฐานของทุกกลุ่ม */
export interface CustomerListPage {
  readonly items: readonly Customer[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/customers`
 *
 * `status` เป็น optional จริง ๆ ตาม contract — **ไม่ส่งเลย** ต่างจากส่ง `null`
 * หรือสตริงว่าง ซึ่ง `extra = "forbid"` + enum จะปฏิเสธด้วย `400`
 *
 * `address`/`contacts` ถูกเพิ่มโดย D-126 — **ส่งเมื่อกรอกเท่านั้น** ไม่มีค่าที่ "ล้าง"
 * แบบ `PATCH` เพราะยังไม่มีค่าเดิมให้ล้าง: ที่อยู่ว่างหรือไม่มีผู้ติดต่อเลยคือไม่ส่ง
 * คีย์นั้นไปทั้งคีย์
 */
export interface CreateCustomerBody {
  readonly customer_code: string;
  readonly customer_name: string;
  readonly status?: string;
  readonly address?: string;
  readonly contacts?: readonly CustomerContact[];
}

/**
 * body ของ `PATCH /v1/customers/{customer_id}` — **ไม่มี `customer_code`**
 *
 * ทุกคีย์เป็น optional เพราะ `PATCH` พาไปเฉพาะสิ่งที่เปลี่ยน คีย์ที่ไม่ได้ส่งถูก
 * ปล่อยไว้ตามเดิม ("absent is not null") — ยกเว้น `address` ที่ `null` แปลว่า
 * ล้างที่อยู่จริง ๆ (D-125, schema ฝั่งเซิร์ฟเวอร์รับ `null` ได้)
 *
 * `contacts` ที่ส่งไปต้องเป็น**ทั้งรายการ** ไม่ใช่ส่วนต่าง — `PATCH` แทนที่ทั้งชุด
 * และ slot ที่ไม่อยู่ในรายการที่ส่งถูกลบ (D-125) ห้ามส่ง `null` เพราะถูกปฏิเสธ
 * ผู้ติดต่อว่างทั้งหมดส่งเป็น `[]`
 */
export interface UpdateCustomerBody {
  readonly customer_name?: string;
  readonly status?: string;
  readonly address?: string | null;
  readonly contacts?: readonly CustomerContact[];
}
